import {
    NGX_MAT_DATE_FORMATS,
    NgxMatDateAdapter,
    NgxMatDateFormats,
} from '@angular-material-components/datetime-picker';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
} from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { CustomNgxDatetimeAdapter } from '@klickdata/core/application/src/custom-datetime-adapter';
import { OnDestroyHandler } from 'apps/klickdata/src/app/shared/onDestroy-handler/onDestroy-handler';
import { filter, takeUntil } from 'rxjs';

const CUSTOM_DATE_FORMATS: NgxMatDateFormats = {
    parse: {
        // Match display format first so manual typing (e.g. "2025-03-01 14:30") parses correctly.
        // 'L' = locale date only; without this, typing the displayed value would be parsed with 'L' and fail → null.
        dateInput: ['YYYY-MM-DD HH:mm', 'YYYY-MM-DD', 'L LT', 'L'],
    },
    display: {
        dateInput: 'YYYY-MM-DD HH:mm',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMMM YYYY',
    },
};

@Component({
    selector: 'app-occasions-dates-manager',
    templateUrl: './occasions-dates-manager.component.html',
    styleUrls: ['./occasions-dates-manager.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NgxMatDateAdapter,
            useClass: CustomNgxDatetimeAdapter,
            deps: [MAT_DATE_LOCALE],
        },

        { provide: NGX_MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS },
    ],
})
export class OccasionsDatesManagerComponent extends OnDestroyHandler implements OnInit, OnChanges {
    @Input() alwaysOpenControl: FormControl;
    @Input() displayedDates: 'occasions' | 'enrollment' = 'occasions';
    @Input() overdueOccasions: FormArray;
    @Input() dateOccasions: FormArray;
    @Input() onTheFly: boolean = false;
    public showOverdueOccasions = false;
    private subscriptionsSetup = new Set<FormGroup>();
    private observedFormArrays = new Set<FormArray>();

    constructor(private fb: FormBuilder, private cd: ChangeDetectorRef) {
        super();
    }
    ngOnInit(): void {
        if (this.alwaysOpenControl) {
            this.alwaysOpenControl.valueChanges.pipe(takeUntil(this.destroy)).subscribe((value) => {
                if (value && this.alwaysOpenControl.dirty) {
                    this.dateOccasions.clear();
                }
            });
        }
        if (this.dateOccasions) {
            this.dateOccasions.valueChanges.pipe(takeUntil(this.destroy)).subscribe((dates) => {
                this.cd.markForCheck();
            });
        }

        // Set up subscriptions if FormArrays are already available
        this.initializeSubscriptions();
    }

    ngOnChanges(changes: SimpleChanges): void {
        // Set up subscriptions when FormArrays are first set or change
        if (changes['dateOccasions'] || changes['overdueOccasions']) {
            this.initializeSubscriptions();
        }
    }

    /**
     * Initializes subscriptions for all FormArrays
     */
    private initializeSubscriptions(): void {
        if (this.dateOccasions) {
            this.setupStartDateSubscriptions(this.dateOccasions);
            this.observeFormArray(this.dateOccasions);
        }
        if (this.overdueOccasions) {
            this.setupStartDateSubscriptions(this.overdueOccasions);
            this.observeFormArray(this.overdueOccasions);
        }
    }
    keepFirstDateOnly(): void {
        while (this.dateOccasions.length > 1) {
            this.dateOccasions.removeAt(1);
        }
        this.dateOccasions.markAsDirty();
    }
    addDate(index: number = -1) {
        const newOccasion = this.createNewOccasion(index >= 0 ? this.dateOccasions.at(index).value : undefined);
        this.dateOccasions.push(newOccasion);
        if (!this.subscriptionsSetup.has(newOccasion)) {
            this.setupStartDateSubscription(newOccasion);
            this.subscriptionsSetup.add(newOccasion);
        }
        this.dateOccasions.markAsDirty();
        this.cd.markForCheck();
    }
    // duplicateDate(i: number) {
    //     const newOccasion = this.dateOccasions.at(i).value;
    //     this.dateOccasions.push(this.fb.group(newOccasion));
    //     this.dateOccasions.markAsDirty();
    //     this.cd.markForCheck();
    // }
    private createNewOccasion(occasion?: any): FormGroup {
        return this.fb.group({
            id: [occasion?.id ?? null],
            start_date: [occasion?.start_date ?? ''],
            end_date: [occasion?.end_date ?? ''],
            application_start_date: [occasion?.application_start_date ?? ''],
            application_end_date: [occasion?.application_end_date ?? ''],
            total_seats: [occasion?.total_seats ?? 0],
            unlimited_seats: [occasion?.unlimited_seats ?? true],
            location: [occasion?.location ?? ''],
            grib: [occasion?.grib ?? ''],
            activity: [occasion?.activity ?? ''],
            other: [occasion?.other ?? ''],
            target_group: [occasion?.target_group ?? ''],
            expanded: [false],
        });
    }
    removeDate(i: number) {
        this.dateOccasions.removeAt(i);
        this.dateOccasions.markAsDirty();
        this.cd.markForCheck();
    }
    get occasionDatesErrors(): { [key: string]: string[] } | null {
        return this.dateOccasions?.errors?.occasionDates ?? null;
    }

    /**
     * Observes a FormArray for added controls so we can attach subscriptions lazily
     */
    private observeFormArray(formArray: FormArray): void {
        if (!formArray || this.observedFormArrays.has(formArray)) {
            return;
        }
        this.observedFormArrays.add(formArray);
        formArray.valueChanges.pipe(takeUntil(this.destroy)).subscribe(() => {
            this.setupStartDateSubscriptions(formArray);
        });
    }

    /**
     * Sets up start_date subscriptions for all form groups in a FormArray
     */
    private setupStartDateSubscriptions(formArray: FormArray): void {
        if (!formArray) {
            return;
        }
        formArray.controls.forEach((control) => {
            if (control instanceof FormGroup && !this.subscriptionsSetup.has(control)) {
                this.setupStartDateSubscription(control);
                this.subscriptionsSetup.add(control);
            }
        });
    }

    /**
     * Sets up a subscription to auto-fill end_date, application_start_date, and application_end_date
     * when start_date is set and the user had no dates entered (target fields empty).
     * Rules: end_date = start + 1 day at 17:00; application_start_date = start - 30 days at 8:00;
     * application_end_date = start - 14 days at 17:00.
     */
    private setupStartDateSubscription(formGroup: FormGroup): void {
        const startDateControl = formGroup.get('start_date');
        const endDateControl = formGroup.get('end_date');
        const applicationStartControl = formGroup.get('application_start_date');
        const applicationEndControl = formGroup.get('application_end_date');

        if (!startDateControl) {
            return;
        }

        startDateControl.valueChanges
            .pipe(
                filter((startDate) => this.isValidDate(startDate)),
                takeUntil(this.destroy)
            )
            .subscribe((startDate) => {
                const startDateObj = this.toDate(startDate);
                if (!startDateObj) {
                    return;
                }

                if (endDateControl && this.isControlEmpty(endDateControl)) {
                    const endDate = this.addDays(startDateObj, 0);
                    this.setTime(endDate, 17, 0);
                    endDateControl.setValue(endDate, { emitEvent: false });
                } else if (endDateControl && !this.isControlEmpty(endDateControl)) {
                    const endDateObj = this.toDate(endDateControl.value);
                    if (endDateObj && endDateObj.getTime() < startDateObj.getTime()) {
                        const endDate = this.addDays(startDateObj, 0);
                        this.setTime(endDate, 17, 0);
                        endDateControl.setValue(endDate, { emitEvent: false });
                    }
                }
                if (applicationStartControl && this.isControlEmpty(applicationStartControl)) {
                    const appStart = this.addDays(startDateObj, -30);
                    this.setTime(appStart, 8, 0);
                    applicationStartControl.setValue(appStart, { emitEvent: false });
                }
                if (applicationEndControl && this.isControlEmpty(applicationEndControl)) {
                    const appEnd = this.addDays(startDateObj, -14);
                    this.setTime(appEnd, 17, 0);
                    applicationEndControl.setValue(appEnd, { emitEvent: false });
                } else if (
                    applicationStartControl &&
                    applicationEndControl &&
                    !this.isControlEmpty(applicationEndControl)
                ) {
                    const appStartObj = this.toDate(applicationStartControl.value);
                    const appEndObj = this.toDate(applicationEndControl.value);
                    if (appStartObj && appEndObj && appEndObj.getTime() < appStartObj.getTime()) {
                        const appEnd = new Date(appStartObj);
                        this.setTime(appEnd, 17, 0);
                        applicationEndControl.setValue(appEnd, { emitEvent: false });
                    }
                }
                this.cd.markForCheck();
            });

        if (applicationStartControl && applicationEndControl) {
            applicationStartControl.valueChanges
                .pipe(
                    filter((appStart) => this.isValidDate(appStart)),
                    takeUntil(this.destroy)
                )
                .subscribe((appStart) => {
                    const appStartObj = this.toDate(appStart);
                    if (!appStartObj || this.isControlEmpty(applicationEndControl)) {
                        return;
                    }
                    const appEndObj = this.toDate(applicationEndControl.value);
                    if (appEndObj && appEndObj.getTime() < appStartObj.getTime()) {
                        const appEnd = new Date(appStartObj);
                        this.setTime(appEnd, 17, 0);
                        applicationEndControl.setValue(appEnd, { emitEvent: false });
                        this.cd.markForCheck();
                    }
                });
        }
    }

    private isControlEmpty(control: { value: unknown }): boolean {
        const v = control.value;
        return v === null || v === '' || v === undefined;
    }

    private setTime(date: Date, hours: number, minutes: number): void {
        date.setHours(hours, minutes, 0, 0);
    }

    private addDays(date: Date | string | unknown, days: number): Date {
        const dateObj = this.toDate(date);
        if (!dateObj) {
            return new Date();
        }
        const result = new Date(dateObj);
        result.setDate(result.getDate() + days);
        return result;
    }

    private toDate(value: Date | string | unknown): Date | null {
        if (!value) {
            return null;
        }
        if (value instanceof Date) {
            return isNaN(value.getTime()) ? null : value;
        }
        if (typeof value === 'string') {
            const d = new Date(value);
            return isNaN(d.getTime()) ? null : d;
        }
        if (value && typeof value === 'object') {
            if ('toDate' in value && typeof (value as { toDate: () => Date }).toDate === 'function') {
                return (value as { toDate: () => Date }).toDate();
            }
            if ('_d' in value) {
                return new Date((value as { _d: Date })._d);
            }
            return new Date(value as Date);
        }
        return null;
    }

    /**
     * Checks if a value is a valid date
     */
    private isValidDate(value: any): boolean {
        if (!value) {
            return false;
        }
        if (value instanceof Date) {
            return !isNaN(value.getTime());
        }
        if (typeof value === 'string') {
            const date = new Date(value);
            return !isNaN(date.getTime());
        }
        // Handle moment objects or other date-like objects
        if (value && typeof value === 'object' && 'toDate' in value) {
            return true;
        }
        if (value && typeof value === 'object' && 'format' in value) {
            return true;
        }
        return false;
    }

    /**
     * Adds one day to a given date
     */
    private addOneDay(date: Date | string | any): Date {
        let dateObj: Date;

        if (date instanceof Date) {
            dateObj = date;
        } else if (typeof date === 'string') {
            dateObj = new Date(date);
        } else if (date && typeof date === 'object') {
            // Handle moment objects or similar
            if ('toDate' in date && typeof date.toDate === 'function') {
                dateObj = date.toDate();
            } else if ('_d' in date) {
                // Moment.js internal date
                dateObj = new Date(date._d);
            } else {
                dateObj = new Date(date);
            }
        } else {
            dateObj = new Date(date);
        }

        const nextDay = new Date(dateObj);
        nextDay.setDate(nextDay.getDate() + 1);
        return nextDay;
    }
}
